import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';

import { useTimeOffReviewActions } from '../../hooks/use-time-off-review-actions';
import { TimeOffRequest } from '../../types/time-off-request';
import { TimeOffReviewDecision } from '../../types/time-off-review';
import { ReviewTimeOffModal } from '../approvals-table/modals/review-time-off-modal';

interface TimeOffApproveActionsProps {
  request: TimeOffRequest;
  /** Called after a decision is submitted (e.g. navigate back to the list). */
  onReviewed: () => void;
}

/**
 * Header Approve / Decline buttons for approve mode. Gating is shared with the
 * approvals row actions cell via {@link useTimeOffReviewActions}; this component
 * only owns the full-text header presentation and the shared review modal.
 *
 * When the current user is queued behind an earlier approver, the buttons are
 * shown disabled with an explanatory tooltip instead of being hidden or left to
 * 403 on click.
 */
export const TimeOffApproveActions = ({ request, onReviewed }: TimeOffApproveActionsProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const { canShowApprove, canShowDecline, hasAnyAction, isBlockedByEarlierApprover } =
    useTimeOffReviewActions(request);
  const [decision, setDecision] = useState<TimeOffReviewDecision | null>(null);

  if (isBlockedByEarlierApprover) {
    return (
      <SimpleTooltip content={t('approvals.detail.waitingForEarlierApproverTooltip')}>
        {/* `disabled:pointer-events-none` on Button blocks the tooltip trigger from receiving
            hover/focus directly, so the span (not the buttons) is the actual trigger. */}
        <span className="flex shrink-0 items-center gap-2" tabIndex={0}>
          <Button variant="outlineDestructive" disabled>
            {t('approvals.detail.decline')}
          </Button>
          <Button disabled>{t('approvals.detail.approve')}</Button>
        </span>
      </SimpleTooltip>
    );
  }

  if (!hasAnyAction) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      {canShowDecline && (
        <Button
          variant="outlineDestructive"
          onClick={() => setDecision(TimeOffReviewDecision.Decline)}
        >
          {t('approvals.detail.decline')}
        </Button>
      )}
      {canShowApprove && (
        <Button onClick={() => setDecision(TimeOffReviewDecision.Approve)}>
          {t('approvals.detail.approve')}
        </Button>
      )}

      {decision && (
        <ReviewTimeOffModal
          request={request}
          decision={decision}
          open
          onClose={() => setDecision(null)}
          onReviewed={onReviewed}
        />
      )}
    </div>
  );
};
